import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { supabase } from "../config/supabaseClient";
import { getLocalData } from "../constant/utils/commonUtils";

// Fetch plan optimizer history
export const fetchPlanOptimizerHistory = createAsyncThunk(
  "planOptimizer/fetchHistory",
  async (arg, { rejectWithValue }) => {
    const companyId = typeof arg === "object" ? arg.companyId : arg;
    const dashboardPreviewOnly =
      typeof arg === "object" ? Boolean(arg.dashboardPreviewOnly) : false;

    try {
      let query = supabase
        .from("plan_optimizer")
        .select("*")
        .eq("company_id", companyId);

      if (dashboardPreviewOnly) {
        query = query.not("dashboard_preview_enabled_at", "is", null);
      }

      const { data, error } = await query.order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching plan optimizer history:", error);
        return rejectWithValue(error.message);
      }

      return data || [];
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const enablePlanOptimizerDashboardPreview = createAsyncThunk(
  "planOptimizer/enableDashboardPreview",
  async (planId, { rejectWithValue }) => {
    try {
      const { data, error } = await supabase.rpc(
        "enable_plan_optimizer_dashboard_preview",
        { p_plan_optimizer_id: planId },
      );
      if (error) return rejectWithValue(error.message);
      return { planId, ...(data || {}) };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const fetchPlanStaleStatus = createAsyncThunk(
  "planOptimizer/fetchStaleStatus",
  async (planId, { rejectWithValue }) => {
    try {
      const { data, error } = await supabase.rpc("is_plan_optimizer_stale", {
        p_plan_optimizer_id: planId,
      });
      if (error) return rejectWithValue(error.message);
      return { planId, ...(data || {}) };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const approvePlanOptimizer = createAsyncThunk(
  "planOptimizer/approve",
  async ({ planId, comment }, { rejectWithValue }) => {
    try {
      const userData = getLocalData("sdfUser") || getLocalData();
      const { data, error } = await supabase.rpc(
        "approve_plan_optimizer_with_history",
        {
          p_plan_optimizer_id: planId,
          p_comment: comment?.trim(),
          p_created_by: userData?.id,
        }
      );
      if (error) return rejectWithValue(error.message);
      return { planId, ...data };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Delete plan optimizer (hard delete)
export const deletePlanOptimizer = createAsyncThunk(
  "planOptimizer/delete",
  async (planId, { rejectWithValue }) => {
    try {
      const { error } = await supabase
        .from("plan_optimizer")
        .delete()
        .eq("id", planId);

      if (error) {
        console.error("Error deleting plan optimizer:", error);
        return rejectWithValue(error.message);
      }

      return planId;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Fetch single plan optimizer by ID
export const fetchPlanOptimizerById = createAsyncThunk(
  "planOptimizer/fetchById",
  async (planId, { rejectWithValue }) => {
    try {
      const { data, error } = await supabase
        .from("plan_optimizer")
        .select("*")
        .eq("id", planId)
        .single();

      if (error) {
        console.error("Error fetching plan optimizer:", error);
        return rejectWithValue(error.message);
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const planOptimizerSlice = createSlice({
  name: "planOptimizer",
  initialState: {
    history: [],
    currentPlan: null,
    loading: false,
    error: null,
    staleByPlanId: {},
  },
  reducers: {
    setCurrentPlan: (state, action) => {
      state.currentPlan = action.payload;
    },
    clearCurrentPlan: (state) => {
      state.currentPlan = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch History
      .addCase(fetchPlanOptimizerHistory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPlanOptimizerHistory.fulfilled, (state, action) => {
        state.history = action.payload;
        state.loading = false;
      })
      .addCase(fetchPlanOptimizerHistory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete Plan
      .addCase(deletePlanOptimizer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deletePlanOptimizer.fulfilled, (state, action) => {
        state.history = state.history.filter(
          (plan) => plan.id !== action.payload
        );
        state.loading = false;
      })
      .addCase(deletePlanOptimizer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch by ID
      .addCase(fetchPlanOptimizerById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPlanOptimizerById.fulfilled, (state, action) => {
        state.currentPlan = action.payload;
        state.loading = false;
      })
      .addCase(fetchPlanOptimizerById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchPlanStaleStatus.fulfilled, (state, action) => {
        const { planId, stale, reasons, population_diff } = action.payload;
        state.staleByPlanId[planId] = {
          stale: Boolean(stale),
          reasons: reasons || [],
          population_diff: population_diff || null,
        };
      })
      .addCase(fetchPlanStaleStatus.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(approvePlanOptimizer.fulfilled, (state, action) => {
        const planId = action.payload?.planId;
        if (planId && state.staleByPlanId[planId]) {
          delete state.staleByPlanId[planId];
        }
      });
  },
});

export const { setCurrentPlan, clearCurrentPlan } = planOptimizerSlice.actions;
export default planOptimizerSlice.reducer;

